import React, { useEffect, useState, useCallback } from 'react';
import Header from '../Components/Common/Header';
import { UserPlus, Eye, Trash2, Filter } from 'lucide-react';
import { API_BASE_URL } from '../apiConfig';
import Toast from '../Components/Common/Toast';
import '../Styles/AccountRequestsPage.css';

const AccountRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [filter, setFilter] = useState('all');
  const [pendingCount, setPendingCount] = useState(0);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  const fetchAccountRequests = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('idToken');
      const response = await fetch(
        `${API_BASE_URL}/account-requests?status=${filter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401 || response.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }

      const data = await response.json();
      if (data.success) {
        setRequests(data.requests);
        setPendingCount(data.pendingCount || 0);
      } else {
        showNotification('error', data.message || 'Failed to fetch requests');
      }
    } catch (error) {
      console.error('Error fetching account requests:', error);
      showNotification('error', 'Failed to fetch account requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAccountRequests();
  }, [fetchAccountRequests]);

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      const token = localStorage.getItem('idToken');
      const response = await fetch(
        `${API_BASE_URL}/account-requests/${requestId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401 || response.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }

      const data = await response.json();
      if (data.success) {
        showNotification('success', 'Request deleted successfully');
        fetchAccountRequests();
      } else {
        showNotification('error', data.message || 'Failed to delete request');
      }
    } catch (error) {
      console.error('Error deleting request:', error);
      showNotification('error', 'Failed to delete request');
    }
  };

  const handleStatusUpdate = async (requestId, status) => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await fetch(
        `${API_BASE_URL}/account-requests/${requestId}/status`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status }),
        }
      );

      if (response.status === 401 || response.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }

      const data = await response.json();
      if (data.success) {
        showNotification('success', `Request ${status} successfully`);
        fetchAccountRequests();
      } else {
        showNotification('error', data.message || 'Failed to update request');
      }
    } catch (error) {
      console.error('Error updating request:', error);
      showNotification('error', 'Failed to update request');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#FEF3C7', color: '#92400E', text: 'Pending' },
      approved: { bg: '#D1FAE5', color: '#065F46', text: 'Approved' },
      rejected: { bg: '#FEE2E2', color: '#991B1B', text: 'Rejected' },
    };

    const statusStyle = statusColors[status] || statusColors.pending;

    return (
      <span
        style={{
          backgroundColor: statusStyle.bg,
          color: statusStyle.color,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
        }}
      >
        {statusStyle.text}
      </span>
    );
  };

  return (
    <div className="dashboard-container">
      <div className="ap-wrap-content">
        <Header
          title="Account Requests"
          description="Manage account creation requests"
          icon={<UserPlus size={28} color="#f3f4f6" />}
        />

        <Toast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification({ type: '', message: '' })}
        />

        {/* Stats and Filters */}
        <div className="account-requests-header">
          <div className="pending-count-card">
            <div className="count-number">{pendingCount}</div>
            <div className="count-label">Pending Requests</div>
          </div>

          <div className="filter-section">
            <Filter size={18} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Requests</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Requests Table */}
        <div className="account-requests-table-container">
          {loading ? (
            <div className="loading-state">Loading requests...</div>
          ) : requests.length === 0 ? (
            <div className="empty-state">
              <UserPlus size={48} color="#9CA3AF" />
              <p>No account requests found</p>
            </div>
          ) : (
            <table className="account-requests-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone Number</th>
                  <th>Status</th>
                  <th>Requested Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td>{request.name}</td>
                    <td>{request.email}</td>
                    <td>{request.phoneNumber}</td>
                    <td>{getStatusBadge(request.status)}</td>
                    <td>{formatDate(request.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        {request.status === 'pending' && (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => handleStatusUpdate(request.id, 'approved')}
                              title="Approve"
                            >
                              <Eye size={16} />
                              Approve
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => handleStatusUpdate(request.id, 'rejected')}
                              title="Reject"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(request.id)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountRequestsPage;

